import { useCallback, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import strings from 'strings';
import { UIStatus } from 'UI/constants/status';

import { SAVED_SEARCH_ACTION_KEYS } from '../constants';
import {
  areDateRangesEqual,
  buildPeriodFromSearch,
  cleanFilters,
  getActiveSearchFromPreferences,
  sanitizeSavedSearch,
  saveActiveSearchPreferences
} from '../utils';

const COPIES = strings.shared.savedSearches;

/**
 * Owns saved-search lifecycle state for list pages.
 * It manages the active search, dirty state, saved period, and saved-search actions.
 * Date UI synchronization is handled separately by the page-specific restore hook.
 */
export const useSavedSearches = ({
  defaultDateTypeFilterId = null,
  isDateEnabled = false,
  preferencesKey
}) => {
  const [initialPreferences] = useState(() => getActiveSearchFromPreferences(preferencesKey) || {});

  const [activeSearch, setActiveSearch] = useState(initialPreferences?.savedSearch || null);
  const [savedPeriod, setSavedPeriod] = useState(
    (isDateEnabled && initialPreferences?.savedPeriod) || null
  );
  const appliedPeriod = isDateEnabled ? savedPeriod || null : null;
  const shouldUseSavedSearchDates = isDateEnabled && Boolean(activeSearch);
  const [isDirty, setIsDirty] = useState(false);
  const [uiState, setUIState] = useState({
    error: null,
    isCreationSavedSearchDrawerOpen: false,
    isEditSavedSearchDrawerOpen: false,
    isSavedSearchDrawerOpen: false,
    isSearching: !!initialPreferences?.savedSearch,
    status: UIStatus.Default
  });

  const savedDateTypeId = isDateEnabled
    ? appliedPeriod?.dateTypeFilter?.id ?? defaultDateTypeFilterId
    : null;

  const savedSearchRestoreKey = useMemo(() => {
    if (!activeSearch?.id) return null;

    return JSON.stringify({
      searchId: activeSearch.id,
      startDate: savedPeriod?.startDate || null,
      endDate: savedPeriod?.endDate || null,
      staticDateRangeId: savedPeriod?.staticDateRangeId || null,
      dateTypeFilterId: savedPeriod?.dateTypeFilter?.id || null
    });
  }, [activeSearch, savedPeriod]);

  const handleApplySearch = ({ search, drawerToClose = null }) => {
    setIsDirty(false);
    setActiveSearch(sanitizeSavedSearch(search));
    setSavedPeriod(buildPeriodFromSearch(search?.search));

    setUIState(prev => {
      const updatedState = { ...prev, isSearching: true };

      if (drawerToClose && Object.prototype.hasOwnProperty.call(prev, drawerToClose)) {
        updatedState[drawerToClose] = false;
      }

      return updatedState;
    });

    saveActiveSearchPreferences(preferencesKey, search);
  };

  const clearActiveSearch = useCallback(() => {
    setActiveSearch(null);
    setSavedPeriod(null);
    setIsDirty(false);
    setUIState(prev => ({ ...prev, isSearching: false }));
    saveActiveSearchPreferences(preferencesKey, null);
  }, [preferencesKey]);

  const handleDeleteSearch = ({ searchId = null, onClearPeriod }) => {
    if (searchId !== activeSearch?.id) return;

    if (savedPeriod && onClearPeriod) onClearPeriod();

    clearActiveSearch();
  };

  const handleEditSearch = (filters, period = null, dateTypeFilterId = null) => {
    if (!activeSearch) return;

    const areSearchesEqual = isEqual(cleanFilters(activeSearch.search), cleanFilters(filters));

    const arePeriodsEqual =
      !isDateEnabled ||
      (!period && !savedPeriod) ||
      (period && savedPeriod && areDateRangesEqual(period, savedPeriod));

    const shouldCompareDateType = isDateEnabled && (period || savedPeriod);
    const isDateTypeEqual = !shouldCompareDateType || savedDateTypeId === dateTypeFilterId;

    const isUnchanged = areSearchesEqual && arePeriodsEqual && isDateTypeEqual;
    setIsDirty(!isUnchanged);
  };

  const handleRemoveSearch = ({ onClearPeriod } = {}) => {
    if (savedPeriod && onClearPeriod) onClearPeriod();

    clearActiveSearch();
  };

  const handleSavedSearchDrawerOpen = useCallback(
    () => setUIState(prev => ({ ...prev, isSavedSearchDrawerOpen: true })),
    []
  );

  const handleCreationSavedSearchDrawerOpen = useCallback(
    () => setUIState(prev => ({ ...prev, isCreationSavedSearchDrawerOpen: true })),
    []
  );

  const handleEditSavedSearchDrawerOpen = () =>
    setUIState(prev => ({ ...prev, isEditSavedSearchDrawerOpen: true }));

  const handleCloseSavedSearchDrawer = () =>
    setUIState(prev => ({ ...prev, isSavedSearchDrawerOpen: false }));

  const handleCloseCreationSavedSearchDrawer = () =>
    setUIState(prev => ({ ...prev, isCreationSavedSearchDrawerOpen: false }));

  const handleCloseEditSavedSearchDrawer = () =>
    setUIState(prev => ({ ...prev, isEditSavedSearchDrawerOpen: false }));

  const handleCloseSavedSearchForm = () => {
    if (uiState.isCreationSavedSearchDrawerOpen) {
      handleCloseCreationSavedSearchDrawer();
    } else {
      handleCloseEditSavedSearchDrawer();
    }
  };

  const getSavedSearchButtonDefinitions = useCallback(
    ({ filters }) => {
      const saveButton = {
        key: SAVED_SEARCH_ACTION_KEYS.saveAsNew,
        props: {
          onClick: handleCreationSavedSearchDrawerOpen,
          text: activeSearch ? COPIES.saveAsNewSearch : COPIES.saveSearch,
          variant: 'text'
        }
      };

      const savedSearchesListButton = {
        key: SAVED_SEARCH_ACTION_KEYS.savedSearches,
        props: {
          onClick: handleSavedSearchDrawerOpen,
          text: COPIES.title,
          variant: 'outlined'
        }
      };

      return isEmpty(filters) ? [savedSearchesListButton] : [saveButton, savedSearchesListButton];
    },
    [activeSearch, handleCreationSavedSearchDrawerOpen, handleSavedSearchDrawerOpen]
  );

  const getRestoredDateTypeFilter = useCallback(
    ({ dateFilters, fallbackDateFilter }) => {
      if (!isDateEnabled) return fallbackDateFilter;

      const targetId = savedPeriod?.dateTypeFilter?.id;
      if (targetId === undefined || targetId === null) return fallbackDateFilter;

      return dateFilters.find(dateFilter => dateFilter.id === targetId) || fallbackDateFilter;
    },
    [isDateEnabled, savedPeriod]
  );

  const buildSavedSearchPayload = useCallback(
    ({ filters, selectedPeriod, dateTypeFilter }) => {
      if (!isDateEnabled) return filters;

      return {
        ...filters,
        ...(selectedPeriod || {}),
        ...(selectedPeriod ? { dateTypeFilter } : {})
      };
    },
    [isDateEnabled]
  );

  return {
    activeSearch,
    appliedPeriod,
    buildSavedSearchPayload,
    getRestoredDateTypeFilter,
    getSavedSearchButtonDefinitions,
    handleApplySearch,
    handleCloseCreationSavedSearchDrawer,
    handleCloseEditSavedSearchDrawer,
    handleCloseSavedSearchDrawer,
    handleCloseSavedSearchForm,
    handleDeleteSearch,
    handleEditSavedSearchDrawerOpen,
    handleEditSearch,
    handleRemoveSearch,
    isDirty,
    savedDateTypeId,
    savedSearchesUiState: uiState,
    savedSearchRestoreKey,
    shouldUseSavedSearchDates
  };
};
